import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Plane,
  Ship,
  Truck,
  Package,
  Clock,
  Shield,
  Globe,
  ArrowRight,
  Warehouse,
  FileCheck,
  Headphones,
  Tag,
  Users,
  Award,
  Calculator,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import '../styles/services-page.css';

const trimDesc = (text, max = 118) => {
  if (!text) return '';
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trim()}…`;
};

/** Core service definitions (used to populate showcase cards). */
const SERVICES_DATA = [
    {
      id: 'fcl',
      name: 'FCL (Full Container Load)',
      description: 'Dedicated container for your cargo',
      features: [
        'Full container dedicated to your shipment',
        'Faster transit times',
        'Lower risk of damage',
        'Ideal for large volume shipments',
        'Custom loading and unloading',
      ],
      benefits: [
        'Cost-effective for large shipments',
        'Complete control over loading',
        'Reduced handling',
        'Faster customs clearance',
      ],
      transitTime: '15-30 days',
      minVolume: '20ft or 40ft container',
      bestFor: 'Large volume shipments, fragile goods, time-sensitive cargo',
    },
    {
      id: 'lcl',
      name: 'LCL (Less than Container Load)',
      description: 'Share container space with other shippers',
      features: [
        'Share container with other shipments',
        'Pay only for space you use',
        'Flexible volume options',
        'Consolidation services',
        'Door-to-door delivery',
      ],
      benefits: [
        'Cost-effective for smaller shipments',
        'No minimum volume requirement',
        'Flexible scheduling',
        'Access to global network',
      ],
      transitTime: '20-35 days',
      minVolume: '1 CBM minimum',
      bestFor: 'Small to medium shipments, cost-conscious shippers, flexible timing',
    },
    {
      id: 'air',
      name: 'Air Cargo',
      description: 'Fastest shipping option available',
      features: [
        'Fastest delivery option',
        'Global network coverage',
        'Real-time tracking',
        'Priority handling',
        'Express delivery options',
      ],
      benefits: [
        'Time-critical shipments',
        'High-value goods',
        'Perishable items',
        'Emergency deliveries',
      ],
      transitTime: '2-5 days',
      minVolume: 'No minimum',
      bestFor: 'Time-sensitive cargo, high-value items, perishables, documents',
    },
    {
      id: 'sea',
      name: 'Sea Cargo',
      description: 'Most economical for large shipments',
      features: [
        'Most cost-effective option',
        'Large volume capacity',
        'Environmentally friendly',
        'Reliable scheduling',
        'Global port coverage',
      ],
      benefits: [
        'Lowest cost per unit',
        'Heavy and oversized cargo',
        'Bulk commodities',
        'Non-urgent shipments',
      ],
      transitTime: '15-45 days',
      minVolume: '1 CBM minimum',
      bestFor: 'Heavy machinery, bulk commodities, non-urgent shipments, oversized cargo',
    },
  ];

const ADDITIONAL_SERVICES_DATA = [
    {
      title: 'Land Transport',
      description: 'Reliable ground transportation for local and regional deliveries',
    },
    {
      title: 'Cargo Insurance',
      description: 'Comprehensive insurance coverage for your valuable shipments',
    },
    {
      title: 'Customs Clearance',
      description: 'Expert customs clearance services for smooth international shipping',
    },
    {
      title: 'Warehousing',
      description: 'Secure storage and distribution services at strategic locations',
    },
    {
      title: 'CBM Calculator',
      description:
        'Convert carton dimensions into cubic meters (m³) for quotes, consolidation, and space planning—cm, inch, or feet.',
    },
  ];

function buildShowcaseCards() {
  const byId = (id) => SERVICES_DATA.find((s) => s.id === id);
  const byTitle = (t) => ADDITIONAL_SERVICES_DATA.find((s) => s.title === t);
  const air = byId('air');
  const sea = byId('sea');
  const lcl = byId('lcl');
  const land = byTitle('Land Transport');
  const wh = byTitle('Warehousing');
  const customs = byTitle('Customs Clearance');
  const cbmTool = byTitle('CBM Calculator');

  return [
    {
      title: 'Air Freight',
      description: trimDesc(`${air?.description || ''} ${air?.features?.[0] || ''} Priority handling worldwide.`),
      Icon: Plane,
      image: '/images/services/air-freight.jpg',
      linkTo: '/contact?service=air-cargo',
    },
    {
      title: 'Sea Freight',
      description: trimDesc(`${sea?.description || ''} ${sea?.features?.[1] || ''} Ideal for bulk and FCL shipments.`),
      Icon: Ship,
      image: '/images/services/sea-freight.jpg',
      linkTo: '/contact?service=sea-cargo',
    },
    {
      title: 'Warehousing',
      description: trimDesc(wh?.description || ''),
      Icon: Warehouse,
      image: '/images/services/warehousing.jpg',
      linkTo: '/contact?service=warehousing',
    },
    {
      title: 'Land Transport',
      description: trimDesc(land?.description || ''),
      Icon: Truck,
      image: '/images/services/land-transport.jpg',
      linkTo: '/contact?service=land-transport',
    },
    {
      title: 'Door to Door Delivery',
      description: trimDesc(
        `${lcl?.description || ''} ${
          lcl?.features?.find((f) => f.toLowerCase().includes('door')) || lcl?.features?.[4] || ''
        }`
      ),
      Icon: Package,
      image: '/images/services/door-to-door.jpg',
      linkTo: '/contact?service=lcl',
    },
    {
      title: 'Customs Clearance',
      description: trimDesc(customs?.description || ''),
      Icon: FileCheck,
      image: '/images/services/customs-clearance.jpg',
      linkTo: '/contact?service=customs-clearance',
      linkLabel: 'Learn more',
    },
    {
      title: 'CBM Calculator',
      description: trimDesc(cbmTool?.description || ''),
      Icon: Calculator,
      image: '/images/services/cbm-calculator.jpg',
      linkTo: '/cbm-calculator',
      linkLabel: 'Open calculator',
    },
  ];
}

const SHOWCASE_CARDS = buildShowcaseCards();

const whyItems = [
  { Icon: Shield, title: 'Secure & Safe', text: 'End-to-end handling you can trust.' },
  { Icon: Clock, title: 'On-Time Delivery', text: 'Schedules built around your deadlines.' },
  { Icon: Globe, title: 'Global Network', text: 'Air, sea, and land across key trade lanes.' },
  { Icon: Headphones, title: '24/7 Support', text: 'Dedicated help when you need answers.' },
  { Icon: Tag, title: 'Competitive Pricing', text: 'Transparent rates and flexible options.' },
];

const stats = [
  { Icon: Package, value: '10K+', label: 'Shipments Delivered' },
  { Icon: Globe, value: '100+', label: 'Countries Covered' },
  { Icon: Users, value: '500+', label: 'Happy Clients' },
  { Icon: Clock, value: '24/7', label: 'Customer Support' },
  { Icon: Award, value: '15+', label: 'Years of Experience' },
];

const Services = () => {
  return (
    <div className="svc-page">
      <section className="svc-hero" aria-labelledby="svc-hero-title">
        <div className="svc-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="svc-hero__overlay" />
        <div className="svc-hero__inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="svc-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              Services
            </p>
            <h1 id="svc-hero-title" className="svc-hero__title">
              <em>Our</em> Services
            </h1>
            <p className="svc-hero__lead">
              Comprehensive cargo solutions designed to meet your shipping needs across the globe.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="svc-shell">
        <section className="svc-offer" aria-labelledby="svc-offer-title">
          <header className="svc-offer__head">
            <span className="svc-offer__eyebrow">What we offer</span>
            <h2 id="svc-offer-title" className="svc-offer__title">
              <span>Reliable Cargo Solutions</span>
            </h2>
            <p className="svc-offer__lead">
              From urgent air freight to economical sea cargo and end-to-end logistics, we connect your business to
              the world with dependable service at every step.
            </p>
          </header>

          <div className="svc-grid">
            {SHOWCASE_CARDS.map((card, index) => {
              const Icon = card.Icon;
              const cardLink = card.linkTo || '/contact';
              return (
                <motion.article
                  key={card.title}
                  className="svc-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06, duration: 0.4 }}
                >
                  <Link to={cardLink} className="svc-card__media" aria-label={`${card.title} — ${card.linkLabel || 'Learn more'}`}>
                    <img src={card.image} alt="" loading="lazy" decoding="async" />
                    <div className="svc-card__icon" aria-hidden>
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <span className="svc-card__media-cta">
                      {card.linkLabel || 'Learn more'}
                      <ArrowRight className="w-4 h-4" aria-hidden />
                    </span>
                  </Link>
                  <div className="svc-card__body">
                    <h3 className="svc-card__title">{card.title}</h3>
                    <p className="svc-card__desc">{card.description}</p>
                    <Link to={cardLink} className="svc-card__link">
                      {card.linkLabel || 'Learn more'}
                      <ArrowRight className="w-4 h-4" aria-hidden />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </section>
      </div>

      <section className="svc-why" aria-labelledby="svc-why-title">
        <div className="svc-why__inner">
          <h2 id="svc-why-title" className="svc-why__title">
            Why Choose Jet Shipping Cargo?
          </h2>
          <div className="svc-why__list">
            {whyItems.map(({ Icon, title, text }) => (
              <div key={title} className="svc-why__item">
                <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                <div>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-stats" aria-label="Company statistics">
        <div className="svc-stats__inner">
          {stats.map(({ Icon, value, label }) => (
            <div key={label} className="svc-stat">
              <div className="svc-stat__icon" aria-hidden>
                <Icon className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <div>
                <span className="svc-stat__num">{value}</span>
                <span className="svc-stat__label">{label}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Services;
